import styled, { createGlobalStyle, css } from 'styled-components';

export const NoScrollGlobalStyle = createGlobalStyle`
    body {
        overflow: hidden;
    }
`;

const COLORS = {
    border: '#d9d9d9',
    primary: '#0d65bf',
    primaryDark: '#005ba8',
    text: '#1b1b1b',
    textMuted: '#666',
    textSecondary: '#3d3d3d',
    footerBackground: 'rgba(244, 244, 244, 0.96)',
    white: '#fff',
    black: '#000',
    error: '#d32f2f'
};

// Responsive breakpoints
const BREAKPOINTS = {
    mobile: '576px',
    tablet: '768px'
};

const media = {
    mobile: `@media (max-width: ${BREAKPOINTS.mobile})`,
    tablet: `@media (max-width: ${BREAKPOINTS.tablet})`
};

const formLabelStyles = css`
    display: inline-block;
    margin-bottom: 2px;
    font-size: 11px;
    line-height: 1.2;
    font-weight: 500;
    color: ${COLORS.textMuted};
`;

const formControlStyles = css`
    height: 35px;
    min-height: 25px;
    padding: 4px 10px;
    font-size: 13px;
    line-height: 1.2;
    box-sizing: border-box;

    ${media.tablet} {
        height: 25px;
        min-height: 25px;
        font-size: 12px;
    }

    ${media.mobile} {
        height: 25px;
        min-height: 25px;
        padding: 4px 6px;
        font-size: 11px;
    }
`;

const footerButtonBaseStyles = css`
    text-align: center;
    padding: 10px 0;
    height: 40px;
    border-radius: 4px;
    font-weight: 500;
`;

export const SlideoutContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    margin-top: 16px;
`;

export const CenteredLoader = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    height: 100%;
`;

export const FuelSavingContent = styled.div`
    flex: 1;
    padding: 0 16px 16px;

    ${media.tablet} {
        padding: 0 14px 14px;
    }

    ${media.mobile} {
        padding: 0 12px 12px;
    }
`;

export const TabsContainer = styled.div`
    display: flex;
    align-items: center;
    border-bottom: 1px solid ${COLORS.border};
    overflow-x: auto;
    white-space: nowrap;
    gap: 4px;
`;

export const TabButton = styled.button<{ $isActive: boolean }>`
    border: 0;
    border-bottom: ${(props) => (props.$isActive ? `3px solid ${COLORS.primaryDark}` : '3px solid transparent')};
    background: transparent;
    color: ${(props) => (props.$isActive ? COLORS.text : COLORS.primary)};
    padding: 10px 12px 9px;
    font-size: 15px;
    font-weight: ${(props) => (props.$isActive ? 500 : 400)};
    cursor: pointer;
    line-height: 1.2;
    flex-shrink: 0;

    &:hover {
        color: ${COLORS.primaryDark};
    }

    &:focus-visible {
        outline: 2px solid ${COLORS.primaryDark};
        outline-offset: 2px;
    }

    ${media.tablet} {
        padding: 8px 10px 7px;
        font-size: 14px;
    }

    ${media.mobile} {
        padding: 6px 8px 5px;
        font-size: 13px;
    }
`;

export const TradePanelsContainer = styled.div`
    margin-top: 12px;
`;

export const TradeSection = styled.div`
    margin-bottom: 12px;
`;

export const TradeHeaderButton = styled.button`
    width: 100%;
    border: 0;
    background: transparent;
    cursor: pointer;
    text-align: left;
    padding: 8px 0;
    display: flex;
    align-items: center;
`;

export const TradeChevron = styled.span<{ $isExpanded: boolean }>`
    display: inline-flex;
    width: 20px;
    height: 20px;
    align-items: center;
    justify-content: center;
    margin-right: 8px;
    color: ${COLORS.primary};
    transform: ${(props) => (props.$isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)')};
    transition: transform 150ms ease;

    &::before {
        content: '';
        width: 8px;
        height: 8px;
        border-right: 2px solid ${COLORS.primary};
        border-bottom: 2px solid ${COLORS.primary};
        transform: rotate(45deg);
        box-sizing: border-box;
    }
`;

export const TradeHeader = styled.span`
    color: ${COLORS.primary};
    font-size: 15px;
    font-weight: 400;
`;

export const TradePanel = styled.div`
    margin-top: 8px;

    ${media.mobile} {
        margin-top: 6px;
    }
`;

export const FormGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    row-gap: 0px;
    column-gap: 16px;
    align-content: start;
    max-width: 100%;

    ${media.tablet} {
        column-gap: 12px;
        row-gap: 2px;
        max-width: 100%;
    }

    ${media.mobile} {
        grid-template-columns: 1fr;
        row-gap: 0;
        gap: 2px;
    }
`;

export const FormField = styled.div`
    width: 100%;
    align-self: start;

    ${media.tablet} {
        max-width: 100%;
    }

    ${media.mobile} {
        width: min(100%, 320px);
        margin: 0 auto;

        & > div {
            margin: 0 !important;
        }

        & * {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
        }

        label {
            margin-bottom: 2px !important;
        }
    }

    label {
        font-weight: 500;
    }

    label[for^='select-input-options'] {
        ${formLabelStyles};
        font-weight: 500 !important;
    }

    [id^='select-input-options'] {
        ${formControlStyles};
        font-family: inherit;
    }

    [id^='select-input-options'] input,
    [id^='select-input-options'] button,
    [id^='select-input-options'] [role='combobox'] {
        height: 100%;
        min-height: 100%;
        box-sizing: border-box;
        font-size: 13px;
        line-height: 1.2;
        font-family: inherit;
    }

    .input-field-wrapper {
        margin-bottom: 0;

        ${media.mobile} {
            margin-bottom: 0 !important;
        }

        label {
            ${formLabelStyles};

            ${media.mobile} {
                font-size: 10px;
            }
        }

        .input-field input {
            ${formControlStyles};
        }

        .input-container {
            padding-bottom: 0.6rem;

            ${media.mobile} {
                padding-bottom: 0 !important;
            }
        }
    }

    &.miles-per-field {
        div#milesPer {
            height: 35px !important;
            min-height: 25px !important;
            font-size: 13px !important;
            line-height: 1 !important;
            display: flex !important;
            align-items: center !important;
            overflow: hidden !important;
        }

        label {
            ${formLabelStyles};
            font-size: 11px !important;
            font-weight: 500 !important;

            ${media.mobile} {
                margin-bottom: 2px;
                font-size: 10px !important;
            }
        }
    }

    &.field-error {
        .input-field-wrapper {
            .input-field input {
                border-color: ${COLORS.error};

                &:focus {
                    border-color: ${COLORS.error};
                    box-shadow: 0 0 0 1px ${COLORS.error};
                }
            }
        }
    }
`;

export const ErrorFormField = styled(FormField)`
    .input-field-wrapper {
        .input-field input {
            border-color: ${COLORS.error};

            &:focus {
                border-color: ${COLORS.error};
                box-shadow: 0 0 0 1px ${COLORS.error};
            }
        }
    }
`;

export const ResultDivider = styled.div`
    margin-top: 12px;
    border-top: 1px solid ${COLORS.border};
    width: 100%;
`;

export const SavingsContainer = styled.div`
    margin-top: 12px;
    margin-left: auto;
    width: max-content;
    max-width: 100%;
    display: grid;
    grid-template-columns: max-content 72px 112px;
    column-gap: 6px;
    row-gap: 6px;
    align-items: center;

    ${media.mobile} {
        max-width: 100%;
        margin-left: auto;
        width: 100%;
        grid-template-columns: max-content 68px 102px;
        column-gap: 6px;
    }
`;

export const SavingsHeaderLabel = styled.span`
    font-size: 14px;
    color: ${COLORS.textSecondary};
    font-weight: 600;
    grid-row: 1 / span 3;
    align-self: start;
    padding-top: 2px;
`;

export const ResultRow = styled.div`
    display: contents;
`;

export const ResultLabel = styled.span`
    font-size: 14px;
    color: ${COLORS.textSecondary};
    font-weight: 600;
    text-align: right;
    justify-self: end;
`;

export const ResultValue = styled.span`
    font-size: 14px;
    color: ${COLORS.text};
    font-weight: 600;
`;

export const ColoredResultValue = styled(ResultValue)<{ $color?: string }>`
    color: ${({ $color }) => $color || 'inherit'};
    text-align: right;
    justify-self: end;
    white-space: nowrap;
`;

export const TradeDivider = styled.div`
    margin-top: 14px;
    border-bottom: 1px solid ${COLORS.border};
`;

export const FooterContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 12px 16px;
    min-height: 24px;
    background-color: ${COLORS.footerBackground};
    border-top: 1px solid ${COLORS.border};
    flex-shrink: 0;
    margin: 0 -20px -20px;
    position: sticky;
    bottom: 0;
    z-index: 2;

    ${media.mobile} {
        padding: 10px 12px;
        justify-content: center;
    }
`;

export const FooterButtonGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;

    ${media.mobile} {
        gap: 6px;
        flex-wrap: wrap;
        justify-content: center;
        width: 100%;
    }

    .cancel-button {
        width: 130px;
        text-align: center;
        padding: 10px 0;
        background-color: transparent;
        border: none;
        border-radius: 0;
        color: ${COLORS.primary};
        font-weight: 500;
        font-size: 15px;

        &:hover {
            background-color: transparent;
        }

        &:focus-visible {
            outline: 2px solid ${COLORS.primaryDark};
            outline-offset: 2px;
        }

        ${media.mobile} {
            width: auto;
            min-width: 80px;
            padding: 8px 12px;
            font-size: 13px;
        }
    }

    .apply-print-button {
        width: 168px;
        ${footerButtonBaseStyles};
        border: 1px solid ${COLORS.primary};
        color: ${COLORS.primary};
        background: ${COLORS.white};

        &:disabled {
            border-color: #999;
            color: #999;
            background: #e0e0e0;
            cursor: not-allowed;
            opacity: 0.7;
        }

        ${media.mobile} {
            width: auto;
            min-width: 100px;
            padding: 8px 12px;
            font-size: 13px;
            height: 36px;
        }
    }

    .apply-button {
        width: 100px;
        margin-right: 15px;
        ${footerButtonBaseStyles};
        border: 1px solid ${COLORS.black};
        background: ${COLORS.primaryDark};
        color: ${COLORS.white};

        &:disabled {
            border-color: #999;
            background: #999;
            color: #e0e0e0;
            cursor: not-allowed;
            opacity: 0.7;
        }

        ${media.mobile} {
            width: auto;
            margin-right: 0;
            min-width: 70px;
            padding: 8px 12px;
            font-size: 13px;
            height: 36px;
        }
    }
`;

export const ApplyPrintButtonContent = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: center;
    width: 100%;
`;
